/**
 * The box in the header: a combobox that suggests organizations with a page
 * as the reader types, and opens one on Enter or click. Nine digits open a
 * page without asking. Requests are numbered, and a reply to an older query
 * is dropped, so fast typing never shows a stale list. An empty answer is
 * asked once more without a trailing S, so a plural typed for a singular
 * name still lands. The input keeps the
 * focus throughout: options are reached by `aria-activedescendant`, not by
 * tabbing, which is the pattern a screen reader expects of a combobox.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { SearchResult } from '../../../src/serve/api.js';
import { api } from '../api.js';
import { Badge, Link } from '../components.js';
import { COPY } from '../copy.js';
import { formatEin } from '../format.js';
import { displayName } from '../names.js';
import { navigate } from '../router.js';
import { planQuery, SEARCH_DEBOUNCE_MS, withoutTrailingS } from './query.js';

interface Option {
  readonly key: string;
  readonly to: string;
  readonly label: string;
  readonly title: string | undefined;
  readonly detail: string | null;
  readonly funder: boolean;
}

type Status = 'idle' | 'loading' | 'ready' | 'empty' | 'unavailable';

export const SearchBox = () => {
  const id = useId();
  const [text, setText] = useState('');
  const [options, setOptions] = useState<readonly Option[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const latest = useRef(0);

  useEffect(() => {
    const plan = planQuery(text);
    latest.current += 1;
    const ticket = latest.current;
    if (plan.kind === 'none') {
      setOptions([]);
      setStatus('idle');
      setActive(null);
      return undefined;
    }
    if (plan.kind === 'ein') {
      setOptions([{ key: plan.ein, to: `/org/${plan.ein}`, label: COPY.search.open(formatEin(plan.ein)), title: undefined, detail: null, funder: false }]);
      setStatus('ready');
      setActive(null);
      return undefined;
    }
    const ask = async (): Promise<SearchResult> => {
      const first = await api.search(plan.q);
      if (first.hits.length > 0) {
        return first;
      }
      // One retry without a trailing S: "St Judes" typed without its apostrophe still finds "ST JUDE",
      // and a name that ends in S is never read as its stem unless it found nothing.
      const retried = withoutTrailingS(plan.q);
      return retried === null ? first : api.search(retried);
    };
    const timer = setTimeout(() => {
      setStatus('loading');
      ask()
        .then((result) => {
          if (ticket !== latest.current) {
            return;
          }
          const next = result.hits.map((hit) => ({
            key: hit.ein,
            to: `/org/${hit.ein}`,
            label: displayName(hit.name) ?? formatEin(hit.ein),
            title: hit.name ?? undefined,
            detail: [hit.city === null ? null : displayName(hit.city), hit.state].filter((part) => part !== null).join(', ') || null,
            funder: hit.funder,
          }));
          setOptions(next);
          setStatus(next.length === 0 ? 'empty' : 'ready');
          setActive(null);
        })
        .catch(() => {
          if (ticket === latest.current) {
            setOptions([]);
            setStatus('unavailable');
          }
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const choose = (option: Option): void => {
    setOpen(false);
    setOptions([]);
    setStatus('idle');
    setActive(null);
    navigate(option.to);
  };

  const onKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' && options.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive(active === null ? 0 : (active + 1) % options.length);
    } else if (event.key === 'ArrowUp' && options.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive(active === null ? options.length - 1 : (active - 1 + options.length) % options.length);
    } else if (event.key === 'Enter') {
      const option = options[active ?? 0];
      if (option !== undefined) {
        event.preventDefault();
        choose(option);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'Tab' && status !== 'empty') {
      // When the empty answer offers its link, Tab moves into it; the wrapper's blur closes the list once focus leaves.
      setOpen(false);
    }
  };

  const listId = `${id}-listbox`;
  const shown = open && (options.length > 0 || status === 'empty' || status === 'unavailable');
  return (
    <div
      className="relative w-full md:w-80"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <input
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={listId}
        aria-activedescendant={active === null ? undefined : `${id}-option-${active}`}
        aria-label={COPY.search.placeholder}
        placeholder={COPY.search.placeholder}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
        data-testid="search-input"
      />
      <div className="sr-only" aria-live="polite">
        {status === 'ready' ? `${options.length} suggestions` : status === 'empty' ? 'No suggestions' : ''}
      </div>
      {shown ? (
        <div className="absolute left-0 right-0 z-10 mt-1 rounded border border-stone-200 bg-white shadow" data-testid="search-list">
          <ul role="listbox" id={listId}>
            {options.map((option, index) => (
              <li
                key={option.key}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={active === index}
                title={option.title}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => {
                  // The input would blur and close the list before a click lands; choosing on mousedown keeps the order right.
                  event.preventDefault();
                  choose(option);
                }}
                className={`cursor-pointer px-2 py-1 text-sm ${active === index ? 'bg-teal-50 text-teal-900' : 'text-stone-800'}`}
              >
                {option.label}
                {option.funder ? (
                  <>
                    {' '}
                    <Badge tone="teal">funder</Badge>
                  </>
                ) : null}
                {option.detail === null ? null : <span className="ml-2 text-xs text-stone-500">{option.detail}</span>}
              </li>
            ))}
          </ul>
          {options.length === 0 ? (
            <p className="px-2 py-1 text-sm text-stone-600">
              {status === 'unavailable' ? COPY.search.unavailable : COPY.search.nothing}{' '}
              {status === 'empty' ? (
                // Mousedown would blur the input and unmount the list before the click lands; the options guard the same way.
                <span onMouseDown={(event) => event.preventDefault()}>
                  <Link to="/about/reading#listing">{COPY.search.why}</Link>
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
